import { REPORT_TYPES } from '../consts/reportTypes';

// Mirrors WORKFLOW_STAGE_KEYS in src/hooks/useWorkflowStages.ts. Duplicated
// here (rather than imported) so this module stays JS-only — useWorkflowStages
// is .ts and pulling it in would couple every consumer to the hook's TS types.
export const WORKFLOW_STAGE_KEYS = {
  DASHBOARD: 'dashboard',
  AIFS: 'aifs',
  JURISDICTIONS: 'jurisdictions',
  SOURCE_FILES: 'source_files',
  EXTRACTION: 'extraction',
  TRANSFORMATION: 'transformation',
  LOOKUP_TABLES: 'lookup_tables',
  OUTPUT_DATA: 'output_data',
  REPORT: 'report',
  APPROVAL: 'approval',
  SUBMISSION: 'submission'
};

export const STAGE_ORDER = [
  WORKFLOW_STAGE_KEYS.DASHBOARD,
  WORKFLOW_STAGE_KEYS.AIFS,
  WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  WORKFLOW_STAGE_KEYS.EXTRACTION,
  WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
  WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  WORKFLOW_STAGE_KEYS.REPORT,
  WORKFLOW_STAGE_KEYS.APPROVAL,
  WORKFLOW_STAGE_KEYS.SUBMISSION
];

const REPORT_PATH_PREFIX = /^\/report\/(fca|esma|pf)\//;

/**
 * Map a React Router location to the workflow stage the user is currently on.
 * Returns null when the page is not a per-period stepper stage (e.g. the
 * Quarters dashboard, project list, integrations page).
 */
export const getStageFromLocation = (pathname, search) => {
  const params = new URLSearchParams(search || '');

  if (REPORT_PATH_PREFIX.test(pathname)) {
    if (params.get('openSubmitDrawer') === 'true') return WORKFLOW_STAGE_KEYS.SUBMISSION;
    if (params.get('openApprovalDrawer') === 'true') return WORKFLOW_STAGE_KEYS.APPROVAL;
    return WORKFLOW_STAGE_KEYS.REPORT;
  }

  if (pathname.startsWith('/output-tables/')) return WORKFLOW_STAGE_KEYS.OUTPUT_DATA;

  if (pathname.startsWith('/data-extraction/')) {
    const tab = params.get('tab');
    if (tab === 'lookup_tables') return WORKFLOW_STAGE_KEYS.LOOKUP_TABLES;
    if (tab === 'transformation') return WORKFLOW_STAGE_KEYS.TRANSFORMATION;
    return WORKFLOW_STAGE_KEYS.EXTRACTION;
  }

  if (pathname.endsWith('/upload-files')) return WORKFLOW_STAGE_KEYS.SOURCE_FILES;
  if (pathname.endsWith('/add-aifs')) return WORKFLOW_STAGE_KEYS.AIFS;

  // Jurisdictions has two URL shapes: the dedicated automation route
  // (/project/quaters/:wf/:p/jurisdictions) and the scratch query form
  // (/project/:wf/jurisdictions?periodId=:p).
  if (pathname.endsWith('/jurisdictions') || pathname.includes('/jurisdictions')) {
    return WORKFLOW_STAGE_KEYS.JURISDICTIONS;
  }

  // Legacy /project/:wf/funds URL — AIFs used to live there; treat as AIFs
  // so a period switch from a legacy bookmark still lands on AddAIFs.
  if (/\/project\/[^/]+\/funds$/.test(pathname)) return WORKFLOW_STAGE_KEYS.AIFS;

  return null;
};

/**
 * Same gates the stepper's `useWorkflowStages` applies when it sets
 * `path = '#'`. A stage is "reachable" for `destPeriod` if the workflow has
 * progressed far enough that landing on the page would be useful.
 *
 * Stages that don't progress-gate (AIFs, Source Files, the extraction
 * family) are always reachable — the destination page renders an empty/
 * placeholder state when there's nothing to show.
 */
export const isStageReachable = (stage, destPeriod, project) => {
  if (!stage || !destPeriod) return false;

  const reportType = project?.report_type;
  const reportProgress = destPeriod?.report_progress_status;
  const approveProgress = destPeriod?.approve_progress_status;
  const submitProgress = destPeriod?.submit_progress_status;

  // Period-switch reachability for the Report stage: as long as the workflow
  // has actually started generating (`In progress` or `Completed`), let the
  // user navigate. The Report page handles its own "still generating…"
  // state. Only `Not started` (or missing) bounces the user back to the
  // closest earlier stage.
  const isReportReachable = !!reportProgress && reportProgress !== 'Not started';

  switch (stage) {
    case WORKFLOW_STAGE_KEYS.DASHBOARD:
    case WORKFLOW_STAGE_KEYS.AIFS:
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
    case WORKFLOW_STAGE_KEYS.EXTRACTION:
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION:
    case WORKFLOW_STAGE_KEYS.LOOKUP_TABLES:
    case WORKFLOW_STAGE_KEYS.OUTPUT_DATA:
      return true;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      return reportType !== REPORT_TYPES.CUSTOM;
    case WORKFLOW_STAGE_KEYS.REPORT:
      return isReportReachable;
    case WORKFLOW_STAGE_KEYS.APPROVAL:
      return isReportReachable || approveProgress === 'Completed';
    case WORKFLOW_STAGE_KEYS.SUBMISSION:
      return submitProgress === 'Completed';
    default:
      return false;
  }
};

/**
 * Filter STAGE_ORDER for the destination period's report type. Mirrors the
 * Conversion / Custom skips in useWorkflowStages.
 */
const orderForReportType = reportType => {
  if (reportType === REPORT_TYPES.CONVERSION) {
    // Conversion has its own visual sequence — Source Files comes BEFORE
    // AIFs / Jurisdictions. Mirrors `useWorkflowStages` exactly so the
    // stepper and the period switcher walk the same order.
    return [
      WORKFLOW_STAGE_KEYS.SOURCE_FILES,
      WORKFLOW_STAGE_KEYS.AIFS,
      WORKFLOW_STAGE_KEYS.JURISDICTIONS,
      WORKFLOW_STAGE_KEYS.REPORT,
      WORKFLOW_STAGE_KEYS.APPROVAL,
      WORKFLOW_STAGE_KEYS.SUBMISSION
    ];
  }
  let order = STAGE_ORDER.filter(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD);
  if (reportType === REPORT_TYPES.CUSTOM) {
    order = order.filter(k => k !== WORKFLOW_STAGE_KEYS.JURISDICTIONS);
  }
  return order;
};

/**
 * Compute the FCA vs ESMA report URL. Form PF + Conversion both use
 * /report/.../{state} so the helper falls back to PF when the report type
 * indicates so. Returns null when a member state can't be derived (forces
 * the caller to keep walking the stage order).
 */
const buildReportPath = (workflowId, periodId, project, jurisdictions) => {
  if (project?.report_type === REPORT_TYPES.FORM_PF) {
    return `/report/pf/us/${workflowId}/${periodId}`;
  }
  const state = jurisdictions?.[0]?.reporting_member_state;
  if (!state) return null;
  const reportKind = state === 'UK' ? 'fca' : 'esma';
  return `/report/${reportKind}/${String(state).toLowerCase()}/${workflowId}/${periodId}`;
};

/**
 * Build the canonical URL for a given stage + period. Mirrors `buildPath` in
 * useWorkflowStages.ts but exposed as a standalone helper. Returns null when
 * the URL can't be built (e.g. REPORT without jurisdictions for a non-Form-PF
 * workflow) — caller walks the stage order to the next reachable stage.
 */
export const buildStagePath = (stage, workflowId, periodId, period, project, jurisdictions) => {
  if (!workflowId || !periodId) return null;
  switch (stage) {
    case WORKFLOW_STAGE_KEYS.DASHBOARD:
      return `/project/quaters/${workflowId}${project?.is_workflow_group ? '?type=grouped' : ''}`;
    case WORKFLOW_STAGE_KEYS.AIFS:
      return `/project/quaters/${workflowId}/${periodId}/add-aifs`;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      // Both scratch and automation flows use the /quaters/... route — the
      // Jurisdictions page reads `useParams()` + `useSearchParams()` to
      // resolve workflowId/periodId either way. Using the unified URL keeps
      // period-switching correct even when the destination period's
      // `template_type` isn't populated in the workflow's periods array (it
      // sometimes comes through as undefined, which previously flipped this
      // branch to the scratch URL for an automation destination).
      return `/project/quaters/${workflowId}/${periodId}/jurisdictions`;
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
      return `/project/quaters/${workflowId}/${periodId}/upload-files`;
    case WORKFLOW_STAGE_KEYS.EXTRACTION:
      return `/data-extraction/${workflowId}/${periodId}?tab=extraction`;
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION:
      return `/data-extraction/${workflowId}/${periodId}?tab=transformation`;
    case WORKFLOW_STAGE_KEYS.LOOKUP_TABLES:
      return `/data-extraction/${workflowId}/${periodId}?tab=lookup_tables`;
    case WORKFLOW_STAGE_KEYS.OUTPUT_DATA:
      return `/output-tables/${workflowId}/${periodId}`;
    case WORKFLOW_STAGE_KEYS.REPORT:
      return buildReportPath(workflowId, periodId, project, jurisdictions);
    case WORKFLOW_STAGE_KEYS.APPROVAL: {
      const base = buildReportPath(workflowId, periodId, project, jurisdictions);
      if (!base) return null;
      return base.includes('?')
        ? `${base}&openApprovalDrawer=true`
        : `${base}?openApprovalDrawer=true`;
    }
    case WORKFLOW_STAGE_KEYS.SUBMISSION: {
      const base = buildReportPath(workflowId, periodId, project, jurisdictions);
      if (!base) return null;
      return base.includes('?') ? `${base}&openSubmitDrawer=true` : `${base}?openSubmitDrawer=true`;
    }
    default:
      return null;
  }
};

/**
 * Pick the best destination URL when the user switches to `newPeriodId`
 * while sitting on a stage page (`requestedStage`).
 *
 * Strategy:
 *   1. If the same stage is reachable for the destination period, navigate
 *      there (best UX — user keeps reading the same kind of content).
 *   2. Otherwise walk STAGE_ORDER backward until we find a reachable stage
 *      that the period type supports. Output Data / Extraction / AIFs are
 *      always reachable, so the walk always terminates.
 *
 * `destJurisdictions` is only required for REPORT / APPROVAL / SUBMISSION on
 * Annex IV / Conversion (FCA vs ESMA depends on the first jurisdiction's
 * reporting_member_state). Pass undefined for other stages; the helper
 * falls through to an earlier stage if jurisdictions are missing.
 */
export const pickPeriodSwitchDestination = ({
  requestedStage,
  workflowId,
  newPeriodId,
  destPeriod,
  project,
  destJurisdictions
}) => {
  const fallback = `/project/quaters/${workflowId}/${newPeriodId}/add-aifs`;
  if (!workflowId || !newPeriodId || !destPeriod) return fallback;

  if (!requestedStage) return fallback;

  const canonical = STAGE_ORDER.filter(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD);
  if (canonical.indexOf(requestedStage) < 0) return fallback;

  const order = orderForReportType(project?.report_type);
  // Anchor "where the user was" directly in the filtered order. The previous
  // canonical-monotonic lookup assumed `order` indices grew with canonical
  // position; Conversion breaks that (Source Files sits before AIFs in the
  // visual order, but trails them in canonical STAGE_ORDER), so we use
  // indexOf instead. When requestedStage was filtered out (e.g. Extraction
  // on a Conversion period), fall back to walking canonical backward for the
  // closest earlier stage that survived the filter and anchor there.
  let startIdx = order.indexOf(requestedStage);
  if (startIdx < 0) {
    const requestedCanonicalIdx = canonical.indexOf(requestedStage);
    for (let ci = requestedCanonicalIdx - 1; ci >= 0; ci -= 1) {
      const idx = order.indexOf(canonical[ci]);
      if (idx >= 0) {
        startIdx = idx;
        break;
      }
    }
  }
  if (startIdx < 0) return fallback;

  for (let i = startIdx; i >= 0; i -= 1) {
    const stage = order[i];
    if (!isStageReachable(stage, destPeriod, project)) continue;
    const path = buildStagePath(
      stage,
      workflowId,
      newPeriodId,
      destPeriod,
      project,
      destJurisdictions
    );
    if (path) return path;
  }

  return fallback;
};
